import { ModelRow } from "@mcleod/core";
import { DataSource } from "./DataSource";

/**
 * Represents a category in the hierarchical categorization structure.
 * Matches the structure returned by the server-side Category class.
 */
export interface Category {
    /** Descriptive name for this category */
    name: string;
    /** The field name used for categorization (null for default/other categories) */
    fieldName: string | null;
    /** The field value that defines this category (null for numeric ranges or default categories) */
    fieldValue: string | null;
    /** Number of items directly in this category (excluding subcategories) */
    itemCount: number;
    /** Nested subcategories (created when a category has more than 30 items) */
    subcategories: Category[];
    /** Full path to this category from root (e.g., "Status > Active > Priority > High") */
    path: string | null;
}

/**
 * Represents a total calculation result for a specific field.
 */
export interface Total {
    /** The field name that was totaled */
    field: string;
    /** Average value */
    average: number;
    /** Number of records included in the calculation */
    count: number;
    /** Maximum value */
    max: number;
    /** Minimum value */
    min: number;
    /** Sum of all values */
    sum: number;
    /** Total number of rows considered */
    total_count: number;
    /** Optional category containing histogram data */
    category?: Category;
}

/**
 * Response structure from the server when requesting categories.
 */
export interface CategorizeResponse {
    /** The categorized results */
    analytics_output: Category[];
    /** Original search results */
    modelRows: ModelRow[];
    /** Other ModelSearchResult properties... */
    actualRowCount?: number;
    summaryData?: ModelRow;
}

/**
 * Response structure from the server when requesting totals.
 */
export interface TotalResponse {
    /** The total calculation result */
    analytics_output: Total;
    /** Original search results */
    modelRows: ModelRow[];
    /** Other ModelSearchResult properties... */
    actualRowCount?: number;
    summaryData?: ModelRow;
}

/**
 * The TableToolAnalytics class provides automatic categorization and totaling of search results from a DataSource.
 * It delegates analytics operations to the server-side which uses LLM-based analysis to discover
 * meaningful categories and calculate totals.
 */
export class TableToolAnalytics {
    private dataSource: DataSource;

    /**
     * Creates a new TableToolAnalytics instance.
     * @param dataSource The DataSource to perform analytics on
     */
    constructor(dataSource: DataSource) {
        this.dataSource = dataSource;
    }

    /**
     * Retrieves and categorizes search results from the DataSource.
     * Uses the lastSearch filter from the DataSource and adds _analytics: "categories"
     * to request server-side categorization. The server organizes results into a
     * hierarchical category tree with 5-20 categories per level, automatically breaking
     * categories with more than 30 items into subcategories.
     *
     * @returns Promise resolving to an array of top-level categories
     * @throws Error if no search has been performed, if the network request fails, or if the server returns an error
     */
    async getCategories(): Promise<Category[]> {
        // Get the DataSource's last search filter
        const baseFilter = this.dataSource.lastSearch;

        if (!baseFilter) {
            throw new Error("No search has been performed on the DataSource yet");
        }

        try {
            // Clone the DataSource to avoid affecting the UI during categorization
            const clonedDataSource = this.cloneDataSource();

            // Add the _analytics parameter to request categorization inside _data
            const filterWithAnalytics = this.addAnalyticsParameter(baseFilter, "categories");

            // Perform the search with categorization request using the cloned DataSource
            const result = await clonedDataSource.search(filterWithAnalytics._data);
            const resultRow = result.modelRows[0];
            const searchResult = resultRow._data as CategorizeResponse;

            // Extract and return the categories from the response
            if (!searchResult || !searchResult.analytics_output) {
                return [];
            }

            return searchResult.analytics_output;
        } catch (error) {
            // Handle specific error types
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error("Unable to retrieve categories due to a network error. Please check your connection and try again.");
            }
            
            // Check for HTTP status errors
            if (error && typeof error === 'object' && 'status' in error) {
                const status = (error as any).status;
                if (status === 500) {
                    throw new Error("The server encountered an error while categorizing results. Please try again later.");
                } else if (status === 503) {
                    throw new Error("The categorization service is temporarily unavailable. Please try again in a few moments.");
                } else if (status >= 400 && status < 500) {
                    throw new Error(`Unable to categorize results: Invalid request (${status}). Please contact support if this persists.`);
                } else if (status >= 500) {
                    throw new Error("The server is experiencing issues. Please try again later.");
                }
            }
            
            // Re-throw with a more user-friendly message if it's a generic error
            if (error instanceof Error) {
                throw new Error(`Failed to retrieve categories: ${error.message}`);
            }
            
            // Fallback for unknown errors
            throw new Error("An unexpected error occurred while retrieving categories. Please try again.");
        }
    }

    /**
     * Retrieves and categorizes search results from the DataSource with custom category fields.
     * Uses the lastSearch filter from the DataSource and adds _analytics: "categories"
     * along with categoryFields to request server-side categorization with specific fields.
     *
     * @param categoryFields Array of field names to use for categorization
     * @returns Promise resolving to an array of top-level categories
     * @throws Error if no search has been performed, if the network request fails, or if the server returns an error
     */
    async getCategoriesWithFields(categoryFields: string[]): Promise<Category[]> {
        // Get the DataSource's last search filter
        const baseFilter = this.dataSource.lastSearch;

        if (!baseFilter) {
            throw new Error("No search has been performed on the DataSource yet");
        }

        try {
            // Clone the DataSource to avoid affecting the UI during categorization
            const clonedDataSource = this.cloneDataSource();

            // Add the _analytics parameter to request categorization with custom fields inside _data
            const filterWithAnalytics = this.addAnalyticsParameter(baseFilter, "categories", { categoryFields });

            // Perform the search with categorization request using the cloned DataSource
            const result = await clonedDataSource.search(filterWithAnalytics._data);
            const resultRow = result.modelRows[0];
            const searchResult = resultRow._data as CategorizeResponse;

            // Extract and return the categories from the response
            if (!searchResult || !searchResult.analytics_output) {
                return [];
            }

            return searchResult.analytics_output;
        } catch (error) {
            // Handle specific error types
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error("Unable to retrieve categories due to a network error. Please check your connection and try again.");
            }
            
            // Check for HTTP status errors
            if (error && typeof error === 'object' && 'status' in error) {
                const status = (error as any).status;
                if (status === 500) {
                    throw new Error("The server encountered an error while categorizing results. Please try again later.");
                } else if (status === 503) {
                    throw new Error("The categorization service is temporarily unavailable. Please try again in a few moments.");
                } else if (status >= 400 && status < 500) {
                    throw new Error(`Unable to categorize results: Invalid request (${status}). Please contact support if this persists.`);
                } else if (status >= 500) {
                    throw new Error("The server is experiencing issues. Please try again later.");
                }
            }
            
            // Re-throw with a more user-friendly message if it's a generic error
            if (error instanceof Error) {
                throw new Error(`Failed to retrieve categories: ${error.message}`);
            }
            
            // Fallback for unknown errors
            throw new Error("An unexpected error occurred while retrieving categories. Please try again.");
        }
    }

    /**
     * Calculates a total for a specific field from the DataSource's search results.
     * Uses the lastSearch filter from the DataSource and adds _analytics: "total"
     * to request server-side total calculation.
     *
     * @param fieldName The name of the field to total
     * @param caption The display caption for the field
     * @returns Promise resolving to the total calculation result
     * @throws Error if no search has been performed, if the network request fails, or if the server returns an error
     */
    async getTotal(fieldName: string, caption: string): Promise<Total> {
        // Get the DataSource's last search filter
        const baseFilter = this.dataSource.lastSearch;

        if (!baseFilter) {
            throw new Error("No search has been performed on the DataSource yet");
        }

        try {
            // Clone the DataSource to avoid affecting the UI during total calculation
            const clonedDataSource = this.cloneDataSource();

            // Add the _analytics parameter to request total calculation inside _data
            const filterWithAnalytics = this.addAnalyticsParameter(baseFilter, "total", {
                fieldName,
                caption
            });

            // Perform the search with total request using the cloned DataSource
            const result = await clonedDataSource.search(filterWithAnalytics._data);
            const resultRow = result.modelRows[0];
            const searchResult = resultRow._data as TotalResponse;

            // Extract and return the total from the response
            if (!searchResult || !searchResult.analytics_output) {
                throw new Error("No total result returned from server");
            }

            return searchResult.analytics_output;
        } catch (error) {
            // Handle specific error types
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error("Unable to calculate totals due to a network error. Please check your connection and try again.");
            }
            
            // Check for HTTP status errors
            if (error && typeof error === 'object' && 'status' in error) {
                const status = (error as any).status;
                if (status === 500) {
                    throw new Error("The server encountered an error while calculating totals. Please try again later.");
                } else if (status === 503) {
                    throw new Error("The totals service is temporarily unavailable. Please try again in a few moments.");
                } else if (status >= 400 && status < 500) {
                    throw new Error(`Unable to calculate totals: Invalid request (${status}). Please contact support if this persists.`);
                } else if (status >= 500) {
                    throw new Error("The server is experiencing issues. Please try again later.");
                }
            }
            
            // Re-throw with a more user-friendly message if it's a generic error
            if (error instanceof Error) {
                throw new Error(`Failed to calculate totals: ${error.message}`);
            }
            
            // Fallback for unknown errors
            throw new Error("An unexpected error occurred while calculating totals. Please try again.");
        }
    }

    /**
     * Creates a clone of the DataSource to perform analytics without affecting the UI.
     * @private
     */
    private cloneDataSource(): DataSource {
        const clonedDataSource = new DataSource({
            url: this.dataSource.url,
            maxResults: this.dataSource.maxResults
        }, this.dataSource.owner);

        // Set the rowConstructor and layoutName directly on the cloned DataSource
        clonedDataSource["_rowConstructor"] = this.dataSource["_rowConstructor"];
        clonedDataSource.layoutName = this.dataSource.layoutName;

        // Remove all bound components from the clone
        // This prevents the analytics search from updating any UI components
        clonedDataSource["_boundComponents"] = [];
        clonedDataSource["_boundSearchComponents"] = [];
        clonedDataSource["componentsByField"] = null;

        return clonedDataSource;
    }

    /**
     * Adds the _analytics parameter to the search filter.
     * @private
     */
    private addAnalyticsParameter(baseFilter: any, analyticsType: string, additionalParams?: any): any {
        const analyticsData = additionalParams
            ? { _analytics: analyticsType, ...additionalParams }
            : { _analytics: analyticsType };

        return {
            ...baseFilter,
            _data: {
                ...baseFilter._data,
                ...analyticsData
            }
        };
    }
}

/**
 * @deprecated Use TableToolAnalytics instead
 */
export class Categorize extends TableToolAnalytics {}
